import React from 'react';
import { linkifyWithLineBreaks } from '../../utils/linkify.tsx';
import { AppIcon, notificationTypeIcon } from '../../icons/appIcons.tsx';

interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'general' | 'payment' | 'event' | 'urgent' | 'maintenance' | 'grounds' | 'horse';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  createdAt: string;
  isRead: boolean;
  expiresAt: string | null;
  creator: {
    firstName: string;
    lastName: string;
  };
}

interface NotificationDetailModalProps {
  notification: Notification;
  onClose: () => void;
  onMarkAsRead?: (notificationId: string) => void;
}

export default function NotificationDetailModal({ notification, onClose, onMarkAsRead }: NotificationDetailModalProps) {
  const getTypeColor = (type: string, priority: string) => {
    if (priority === 'urgent') return 'text-red-600 bg-red-50 border-red-200';
    
    switch (type) {
      case 'grounds': return 'text-green-700 bg-green-50 border-green-300';
      case 'horse': return 'text-amber-700 bg-amber-50 border-amber-300';
      case 'urgent': return 'text-red-600 bg-red-50 border-red-200';
      case 'payment': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'event': return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'maintenance': return 'text-gray-600 bg-gray-50 border-gray-200';
      default: return 'text-blue-600 bg-blue-50 border-blue-200';
    }
  };

  const getPriorityBadge = (priority: string) => {
    const colors = {
      urgent: 'bg-red-100 text-red-800',
      high: 'bg-orange-100 text-orange-800',
      normal: 'bg-blue-100 text-blue-800',
      low: 'bg-gray-100 text-gray-800'
    };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[priority as keyof typeof colors]}`}>
        {priority.charAt(0).toUpperCase() + priority.slice(1)} Priority
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleMarkAsRead = () => {
    if (onMarkAsRead && !notification.isRead) {
      onMarkAsRead(notification.id);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-start mb-6">
            <div className="flex items-start space-x-3 flex-1">
              <AppIcon name={notificationTypeIcon(notification.type)} className="w-8 h-8 flex-shrink-0 mt-1 text-ggaa-green" />
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  {notification.title}
                </h2>
                <div className="flex items-center space-x-3 mb-2">
                  {getPriorityBadge(notification.priority)}
                  <span className="text-sm text-gray-500 capitalize">
                    {notification.type} Announcement
                  </span>
                  {!notification.isRead && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mr-1"></span>
                      Unread
                    </span>
                  )}
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 ml-4 flex-shrink-0"
            >
              <AppIcon name="close" className="w-6 h-6" />
            </button>
          </div>

          {/* Message Content */}
          <div className={`border-l-4 ${getTypeColor(notification.type, notification.priority).split(' ')[2]} p-4 mb-6`}>
            <div className="prose prose-sm max-w-none">
              <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                {linkifyWithLineBreaks(notification.message)}
              </p>
            </div>
          </div>

          {/* Metadata */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Notification Details</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Date:</span>
                <span className="ml-2 text-gray-900 font-medium">
                  {formatDate(notification.createdAt)}
                </span>
              </div>
              <div>
                <span className="text-gray-500">From:</span>
                <span className="ml-2 text-gray-900 font-medium">
                  {notification.creator.firstName} {notification.creator.lastName}
                </span>
              </div>
              <div>
                <span className="text-gray-500">Type:</span>
                <span className="ml-2 text-gray-900 font-medium capitalize">
                  {notification.type}
                </span>
              </div>
              <div>
                <span className="text-gray-500">Status:</span>
                <span className={`ml-2 font-medium ${notification.isRead ? 'text-green-600' : 'text-blue-600'}`}>
                  {notification.isRead ? '✓ Read' : '● Unread'}
                </span>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-6 py-2 text-gray-600 hover:text-gray-800 font-medium"
            >
              Close
            </button>
            {!notification.isRead && onMarkAsRead && (
              <button
                onClick={handleMarkAsRead}
                className="px-6 py-2 bg-ggaa-green text-white rounded-lg hover:bg-ggaa-green/90 transition-colors font-medium"
              >
                ✓ Mark as Read
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
} 